import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchSubscriptions,
  fetchSubscriptionsAll,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  fetchSubscriptionSummary,
} from "./thunks";
import { toast } from "react-toastify";

interface Subscription {
  id: string | number;
  client_id: string | number;
  subscription_id: string | number;
  // Adicione mais campos conforme necessário
}

interface PaginatedSubscriptions {
  data: Subscription[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface SubscriptionsState {
  subscriptions: PaginatedSubscriptions;
  subscriptionsAll: Subscription[]; // Lista de todas as assinaturas sem paginação
  loading: boolean;
  loadingAll: boolean; // Loading específico para subscriptionsAll
  error: string | null;
  subscriptionSummary?: any;
}

const initialState: SubscriptionsState = {
  subscriptions: { data: [] },
  subscriptionsAll: [], // Lista de todas as assinaturas sem paginação
  loading: false,
  loadingAll: false, // Loading específico para subscriptionsAll
  error: null,
  subscriptionSummary: null,
};

const subscriptionsSlice = createSlice({
  name: "subscriptions",
  initialState,
  reducers: {
    clearSubscriptions: (state) => {
      state.subscriptions = { data: [] };
      state.loading = false;
      state.error = null;
    },
    clearSubscriptionsAll: (state) => {
      state.subscriptionsAll = [];
      state.loadingAll = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptions.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            state.subscriptions = { data: payload.data };
          } else {
            state.subscriptions = {
              data: payload.data.data,
              total: payload.total,
              currentPage: payload.current_page,
              perPage: payload.per_page,
            };
          }
        },
      )
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })
      // Fetch Subscriptions All
      .addCase(fetchSubscriptionsAll.pending, (state) => {
        state.loadingAll = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionsAll.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingAll = false;
          const payload = action.payload;
          // Como é without_pagination, sempre será um array direto
          state.subscriptionsAll = payload.data || [];
        },
      )
      .addCase(fetchSubscriptionsAll.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loadingAll = false;
      })

      // Create
      .addCase(createSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.subscriptions.data.push(action.payload.data);
          // Também adicionar ao subscriptionsAll se existir
          state.subscriptionsAll.push(action.payload.data);
          toast.success("Assinatura criada com sucesso!");
        },
      )
      .addCase(createSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao criar assinatura. Tente novamente!");
      })

      // Update
      .addCase(updateSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.subscriptions.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.subscriptions.data[index] = action.payload.data;
          }
          // Também atualizar no subscriptionsAll se existir
          const indexAll = state.subscriptionsAll.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (indexAll !== -1) {
            state.subscriptionsAll[indexAll] = action.payload.data;
          }
          toast.success("Assinatura atualizada com sucesso!");
        },
      )
      .addCase(updateSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao atualizar assinatura!");
      })

      // Delete
      .addCase(deleteSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSubscription.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.subscriptions.data = state.subscriptions.data.filter(
            (s) => String(s.id) !== action.payload.data.id,
          );
          // Também remover do subscriptionsAll
          state.subscriptionsAll = state.subscriptionsAll.filter(
            (s) => String(s.id) !== action.payload.data.id,
          );
          toast.success("Assinatura excluída com sucesso!");
        },
      )
      .addCase(deleteSubscription.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao excluir assinatura!");
      })

      .addCase(fetchSubscriptionSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSubscriptionSummary.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.subscriptionSummary = action.payload?.data;
        },
      )
      .addCase(fetchSubscriptionSummary.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      });
  },
});

export const { clearSubscriptions, clearSubscriptionsAll } =
  subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;
